import { PDP_VARIANT_DISPLAY_ORDER, type VariantKey, type VariantValues } from '@libs/common-types';

export const isVariantGroupDeterminedBy = (
    target: VariantKey,
    source: VariantKey,
    combinations: VariantValues[]
): boolean => {
    if (combinations.length < 2 || combinations.some(combination => !combination[source] || !combination[target])) {
        return false;
    }

    const targetValuesBySource = new Map<string, Set<string>>();
    for (const combination of combinations) {
        const sourceValue = combination[source] as string;
        const targetValue = combination[target] as string;
        const targetValues = targetValuesBySource.get(sourceValue) ?? new Set<string>();
        targetValues.add(targetValue);
        targetValuesBySource.set(sourceValue, targetValues);
    }

    return targetValuesBySource.size > 1 && [...targetValuesBySource.values()].every(values => values.size === 1);
};

/**
 * Returns options that should be labels instead of pickers.
 * Higher-priority options can determine lower-priority labels. A lower-priority
 * option only determines a higher-priority label when that label has one value.
 */
export const getDerivedVariantOptions = (combinations: VariantValues[]): Set<VariantKey> => {
    const presentOptions = PDP_VARIANT_DISPLAY_ORDER.filter(option =>
        combinations.some(combination => Boolean(combination[option]))
    );
    const derivedOptions = new Set<VariantKey>();

    for (const target of presentOptions) {
        const targetPriority = PDP_VARIANT_DISPLAY_ORDER.indexOf(target);
        const targetHasSingleValue =
            new Set(combinations.map(combination => combination[target]).filter(Boolean)).size === 1;
        for (const source of presentOptions) {
            if (source === target || !isVariantGroupDeterminedBy(target, source, combinations)) continue;

            const sourceHasHigherPriority = PDP_VARIANT_DISPLAY_ORDER.indexOf(source) < targetPriority;
            if (targetHasSingleValue || sourceHasHigherPriority) {
                derivedOptions.add(target);
                break;
            }
        }
    }

    return derivedOptions;
};
